/**
 * A channel for "this surface has work that would be lost", consulted by the
 * things that can destroy it.
 *
 * Why a registry and not a route guard. `useSelectWorkspace` commits the new
 * company to the store, persists the choice on the backend and resets the query
 * cache **before** it navigates. A `useBeforeLeave` guard would therefore stop
 * the navigation while leaving the page mounted on a company that is no longer
 * the selected one — the same identity defect the single-request save exists to
 * remove, reintroduced by the defence against it. So the question has to be
 * asked *upstream of the mutation*, and only a shared registry can be reached
 * from there.
 *
 * It is deliberately a single slot: two surfaces holding unsaved work at once
 * cannot happen, because only one is mounted at a time. Registering returns its
 * own disposer, and a disposer only clears the slot if it is still the one it
 * registered — so a late cleanup cannot silently disarm a newer page.
 */
export type UnsavedWorkPrompt = () => Promise<boolean>;

let prompt: UnsavedWorkPrompt | undefined;

export const registerUnsavedWork = (next: UnsavedWorkPrompt) => {
  prompt = next;
  return () => {
    if (prompt === next) prompt = undefined;
  };
};

/**
 * `true` to go ahead, `false` to stay. With nothing registered — every other
 * page — it answers `true` immediately and costs nothing.
 */
export const confirmLeavingUnsavedWork = (): Promise<boolean> =>
  prompt ? prompt() : Promise.resolve(true);
